import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentMethods = ({ paymentMethods, onAddMethod, onRemoveMethod, onSetDefault }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [newMethod, setNewMethod] = useState({
    type: '',
    details: {},
    nickname: ''
  });

  const getMethodIcon = (type) => {
    switch (type) {
      case 'credit_card': return 'CreditCard';
      case 'bank_account': return 'Building2';
      case 'paypal': return 'Wallet';
      case 'crypto': return 'Bitcoin';
      case 'wire_transfer': return 'Banknote';
      default: return 'DollarSign';
    }
  };

  const getMethodColor = (type) => {
    switch (type) {
      case 'credit_card': return 'text-blue-600 bg-blue-50';
      case 'bank_account': return 'text-green-600 bg-green-50';
      case 'paypal': return 'text-purple-600 bg-purple-50';
      case 'crypto': return 'text-orange-600 bg-orange-50';
      case 'wire_transfer': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSecurityBadge = (verified, encrypted) => {
    if (verified && encrypted) return { text: 'Verified & Encrypted', color: 'text-green-600 bg-green-50' };
    if (verified) return { text: 'Verified', color: 'text-blue-600 bg-blue-50' };
    if (encrypted) return { text: 'Encrypted', color: 'text-gray-600 bg-gray-50' };
    return { text: 'Pending Verification', color: 'text-amber-600 bg-amber-50' };
  };

  const formatCardNumber = (number) => {
    return `**** **** **** ${number?.slice(-4)}`;
  };

  const formatAccountNumber = (number) => {
    return `****${number?.slice(-4)}`;
  };

  const handleAddMethod = (e) => {
    e?.preventDefault();
    onAddMethod(newMethod);
    setShowAddModal(false);
    setNewMethod({
      type: '',
      details: {},
      nickname: ''
    });
  };

  const handleRemoveMethod = () => {
    if (selectedMethod) {
      onRemoveMethod(selectedMethod?.id);
      setShowRemoveModal(false);
      setSelectedMethod(null);
    }
  };

  const renderMethodDetails = (method) => {
    switch (method?.type) {
      case 'credit_card':
        return (
          <div>
            <div className="font-medium text-gray-900">{method?.details?.brand} {formatCardNumber(method?.details?.number)}</div>
            <div className="text-sm text-gray-600">Expires {method?.details?.expiryMonth}/{method?.details?.expiryYear}</div>
          </div>
        );
      case 'bank_account':
        return (
          <div>
            <div className="font-medium text-gray-900">{method?.details?.bankName}</div>
            <div className="text-sm text-gray-600">Account {formatAccountNumber(method?.details?.accountNumber)}</div>
          </div>
        );
      case 'paypal':
        return (
          <div>
            <div className="font-medium text-gray-900">PayPal</div>
            <div className="text-sm text-gray-600">{method?.details?.email}</div>
          </div>
        );
      case 'crypto':
        return (
          <div>
            <div className="font-medium text-gray-900">{method?.details?.currency} Wallet</div>
            <div className="text-sm text-gray-600 font-mono">{method?.details?.address?.slice(0, 20)}...</div>
          </div>
        );
      case 'wire_transfer':
        return (
          <div>
            <div className="font-medium text-gray-900">Wire Transfer</div>
            <div className="text-sm text-gray-600">{method?.details?.bankName} - {method?.details?.swiftCode}</div>
          </div>
        );
      default:
        return (
          <div>
            <div className="font-medium text-gray-900">{method?.nickname || 'Payment Method'}</div>
            <div className="text-sm text-gray-600">Details not available</div>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Payment Methods</h2>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddModal(true)}
          >
            Add Method
          </Button>
        </div>
      </div>
      <div className="p-6">
        {paymentMethods?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
            <p className="text-gray-600 mb-4">Add a payment method to start depositing and withdrawing funds.</p>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowAddModal(true)}
            >
              Add Payment Method
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {paymentMethods?.map((method) => {
              const securityBadge = getSecurityBadge(method?.verified, method?.encrypted);
              
              return (
                <div
                  key={method?.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full ${getMethodColor(method?.type)}`}>
                        <Icon name={getMethodIcon(method?.type)} size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          {renderMethodDetails(method)}
                          {method?.isDefault && (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 mt-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${securityBadge?.color}`}>
                            {securityBadge?.text}
                          </span>
                          
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Icon name="Calendar" size={12} />
                            <span>Added {new Date(method.addedAt)?.toLocaleDateString()}</span>
                          </div>
                        </div>

                        {method?.nickname && (
                          <div className="mt-2 text-sm text-gray-600">
                            Nickname: {method?.nickname}
                          </div>
                        )}

                        {method?.limits && (
                          <div className="mt-2 text-xs text-gray-500">
                            Daily limit: ${method?.limits?.daily?.toLocaleString()} | 
                            Monthly limit: ${method?.limits?.monthly?.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!method?.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onSetDefault(method?.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Set Default
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Settings"
                        onClick={() => {
                          // Handle edit method
                          console.log('Edit method:', method?.id);
                        }}
                      />
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => {
                          setSelectedMethod(method);
                          setShowRemoveModal(true);
                        }}
                        className="text-red-600 hover:text-red-700"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Security Information */}
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={20} className="text-green-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-900 mb-1">Your Payment Security</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• All payment data is encrypted with bank-level security</li>
                <li>• We never store your full card numbers or sensitive details</li>
                <li>• PCI DSS compliant payment processing</li>
                <li>• 24/7 fraud monitoring and protection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add Payment Method</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowAddModal(false)}
                />
              </div>
            </div>

            <form onSubmit={handleAddMethod} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method Type
                  </label>
                  <select
                    value={newMethod?.type}
                    onChange={(e) => setNewMethod({...newMethod, type: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="credit_card">Credit/Debit Card</option>
                    <option value="bank_account">Bank Account</option>
                    <option value="paypal">PayPal</option>
                    <option value="crypto">Cryptocurrency</option>
                    <option value="wire_transfer">Wire Transfer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nickname (Optional)
                  </label>
                  <input
                    type="text"
                    value={newMethod?.nickname}
                    onChange={(e) => setNewMethod({...newMethod, nickname: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., My Business Card"
                  />
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start space-x-2">
                    <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      You'll be redirected to a secure page to enter your payment details. 
                      We never store sensitive information on our servers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1"
                >
                  Continue
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Remove Payment Method Modal */}
      {showRemoveModal && selectedMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="Trash2" size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Remove Payment Method</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${getMethodColor(selectedMethod?.type)}`}>
                    <Icon name={getMethodIcon(selectedMethod?.type)} size={16} />
                  </div>
                  <div>
                    {renderMethodDetails(selectedMethod)}
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to remove this payment method? You won't be able to use it for future transactions.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRemoveModal(false)}
                  className="flex-1"
                >
                  Keep Method
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRemoveMethod}
                  className="flex-1"
                >
                  Remove Method
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;